import React, { useState, useEffect } from 'react';
import api from '../api/axios';

const Speakers = () => {
    const [speakers, setSpeakers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentSpeaker, setCurrentSpeaker] = useState({
        name: '', profession: '', profile: '', email: '', photo_url: '', country: '', status: 'active'
    });
    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
        fetchSpeakers();
    }, []);

    const fetchSpeakers = async () => {
        setIsLoading(true);
        try {
            const response = await api.get('/speakers');
            setSpeakers(response.data);
        } catch (error) {
            console.error("Error fetching", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            if (isEditing) {
                await api.put(`/speakers/${currentSpeaker.id}`, currentSpeaker);
            } else {
                await api.post('/speakers', currentSpeaker);
            }
            fetchSpeakers();
            closeModal();
        } catch (error) {
            console.error("Error saving", error);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("¿Estás seguro de eliminar este conferencista?")) {
            try {
                await api.delete(`/speakers/${id}`);
                fetchSpeakers();
            } catch (error) {
                console.error("Error deleting", error);
            }
        }
    };

    const openModal = (speaker = null) => {
        if (speaker) {
            setCurrentSpeaker(speaker);
            setIsEditing(true);
        } else {
            setCurrentSpeaker({
                name: '', profession: '', profile: '', email: '', photo_url: '', country: '', status: 'active'
            });
            setIsEditing(false);
        }
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    return (
        <div>
            <header className="flex justify-between items-center mb-10">
                <div>
                    <h1 className="text-4xl font-extrabold text-slate-900 tracking-tight">Conferencistas</h1>
                    <p className="text-slate-500 mt-2">Gestiona los perfiles de los ponentes del evento.</p>
                </div>
                <button 
                    onClick={() => openModal()}
                    className="bg-blue-600 text-white px-6 py-3 rounded-2xl font-bold shadow-lg shadow-blue-900/20 hover:bg-blue-500 transition-all flex items-center transform hover:scale-105 active:scale-95"
                >
                    <span className="mr-2 text-xl">+</span> Agregar Conferencista
                </button>
            </header>

            {isLoading ? (
                <div className="text-center py-20 text-slate-400">Cargando conferencistas...</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {speakers.map((s) => (
                        <div key={s.id} className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 relative group overflow-hidden">
                            <div className="flex items-start justify-between mb-6">
                                <div className="w-20 h-20 rounded-2xl bg-blue-50 border-2 border-white shadow-xl overflow-hidden shadow-blue-900/5">
                                    <img 
                                        src={s.photo_url || `https://ui-avatars.com/api/?name=${s.name}&background=random`} 
                                        alt={s.name} 
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <span className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest ${s.status === 'active' ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'}`}>
                                    {s.status}
                                </span>
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 tracking-tight">{s.name}</h3>
                            <p className="text-blue-600 text-sm font-semibold mb-1">{s.profession}</p>
                            {s.country && <p className="text-slate-400 text-xs font-bold uppercase tracking-wider mb-4 flex items-center"><span className="mr-1">📍</span>{s.country}</p>}
                            <p className="text-slate-500 text-sm line-clamp-2 h-10 mb-6 leading-relaxed">{s.profile}</p>
                            
                            <div className="flex space-x-3 pt-6 border-t border-slate-50">
                                <button 
                                    onClick={() => openModal(s)}
                                    className="flex-grow bg-slate-50 text-slate-600 px-4 py-2.5 rounded-xl text-sm font-bold hover:bg-blue-50 hover:text-blue-600 transition-colors"
                                >
                                    Editar
                                </button>
                                <button 
                                    onClick={() => handleDelete(s.id)}
                                    className="bg-red-50 text-red-500 px-4 py-2.5 rounded-xl text-sm font-bold hover:bg-red-100 transition-colors"
                                >
                                    ❌
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md flex items-center justify-center p-6 z-[100] animate-fadeIn">
                    <div className="bg-white rounded-[40px] shadow-2xl w-full max-w-2xl overflow-y-auto max-h-[90vh] animate-slideUp">
                        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-8 text-white relative sticky top-0 z-10">
                            <h2 className="text-3xl font-black">{isEditing ? 'Editar' : 'Nuevo'} Conferencista</h2>
                            <button onClick={closeModal} className="absolute top-8 right-8 text-white/50 hover:text-white text-2xl">✕</button>
                        </div>
                        <form onSubmit={handleSave} className="p-10 grid grid-cols-2 gap-6">
                            <div className="col-span-2 md:col-span-1">
                                <label className="block text-slate-700 text-sm font-bold mb-2 pl-1">Nombre Completo</label>
                                <input 
                                    className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-100 focus:bg-white transition-all shadow-inner"
                                    value={currentSpeaker.name}
                                    onChange={e => setCurrentSpeaker({...currentSpeaker, name: e.target.value})}
                                    required
                                />
                            </div>
                            <div className="col-span-2 md:col-span-1">
                                <label className="block text-slate-700 text-sm font-bold mb-2 pl-1">Profesión / Título</label>
                                <input 
                                    className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-100 focus:bg-white transition-all shadow-inner"
                                    value={currentSpeaker.profession}
                                    onChange={e => setCurrentSpeaker({...currentSpeaker, profession: e.target.value})}
                                />
                            </div>
                            <div className="col-span-2">
                                <label className="block text-slate-700 text-sm font-bold mb-2 pl-1">Perfil Profesional</label>
                                <textarea 
                                    className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl h-32 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:bg-white transition-all shadow-inner"
                                    value={currentSpeaker.profile}
                                    onChange={e => setCurrentSpeaker({...currentSpeaker, profile: e.target.value})}
                                />
                            </div>
                            <div className="col-span-2 md:col-span-1">
                                <label className="block text-slate-700 text-sm font-bold mb-2 pl-1">Email</label>
                                <input 
                                    className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-100 focus:bg-white transition-all shadow-inner"
                                    type="email"
                                    value={currentSpeaker.email}
                                    onChange={e => setCurrentSpeaker({...currentSpeaker, email: e.target.value})}
                                />
                            </div>
                            <div className="col-span-2 md:col-span-1">
                                <label className="block text-slate-700 text-sm font-bold mb-2 pl-1">País</label>
                                <input 
                                    className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-100 focus:bg-white transition-all shadow-inner"
                                    value={currentSpeaker.country}
                                    placeholder="Ej. Colombia, México"
                                    onChange={e => setCurrentSpeaker({...currentSpeaker, country: e.target.value})}
                                />
                            </div>
                            <div className="col-span-2 md:col-span-1">
                                <label className="block text-slate-700 text-sm font-bold mb-2 pl-1">Estado</label>
                                <select 
                                    className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-100 focus:bg-white transition-all shadow-inner appearance-none"
                                    value={currentSpeaker.status}
                                    onChange={e => setCurrentSpeaker({...currentSpeaker, status: e.target.value})}
                                >
                                    <option value="active">Activo</option>
                                    <option value="inactive">Inactivo</option>
                                </select>
                            </div>
                            <div className="col-span-2">
                                <label className="block text-slate-700 text-sm font-bold mb-2 pl-1">URL Foto (Opcional)</label>
                                <input 
                                    className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-100 focus:bg-white transition-all shadow-inner"
                                    placeholder="https://ejemplo.com/foto.jpg"
                                    value={currentSpeaker.photo_url}
                                    onChange={e => setCurrentSpeaker({...currentSpeaker, photo_url: e.target.value})}
                                />
                            </div>
                            <div className="col-span-2 pt-6 flex justify-end space-x-4">
                                <button type="button" onClick={closeModal} className="px-8 py-4 text-slate-500 font-bold hover:text-slate-800 transition-colors">Cancelar</button>
                                <button type="submit" className="bg-blue-600 text-white px-12 py-4 rounded-2xl font-black shadow-xl shadow-blue-900/20 hover:bg-blue-500 transition-all active:scale-95">
                                    {isEditing ? 'Actualizar Cambios' : 'Crear Perfil'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Speakers;
